import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, MapPin, Phone, Hexagon, Code, Monitor, Lock, Zap } from 'lucide-react';
import { motion } from 'framer-motion';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Tech grid component
  const TechGrid = () => (
    <div className="absolute inset-0 z-0">
      <div className="absolute w-full h-full grid grid-cols-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-r border-gray-300 h-full"></div>
        ))}
      </div>
      <div className="absolute w-full h-full grid grid-rows-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-b border-gray-300 w-full"></div>
        ))}
      </div>
    </div>
  );

  const FooterLink = ({ to, children }) => (
    <Link to={to} className="text-gray-500 hover:text-gray-300 transition-colors flex items-center group">
      <span className="relative">
        {children}
        <motion.div
          className="absolute left-0 right-0 h-px bg-gray-700/50 bottom-0 origin-left"
          initial={{ scaleX: 0 }}
          whileHover={{ scaleX: 1 }}
          transition={{ duration: 0.3 }}
        />
      </span>
      <motion.div
        className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        animate={{ x: [0, 4, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, repeatType: 'loop', ease: 'easeInOut' }}
      >
        <Hexagon size={8} className="text-gray-500" />
      </motion.div>
    </Link>
  );

  return (
    <footer className="bg-black border-t border-gray-800/50 relative overflow-hidden">
      {/* Decorative elements */}
      <TechGrid />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-700/30 to-transparent"></div>

      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block relative group overflow-hidden mb-2">
              <img
                src="/images/reticuli-logo.png"
                alt="Reticuli Technologies"
                className="h-32 w-auto filter brightness-125 contrast-125"
              />
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-400/20 to-transparent"
                animate={{ x: ['-100%', '100%'] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: 'loop',
                  ease: 'easeInOut',
                }}
              />
            </div>

            <div className="flex space-x-4">
              {/* Social Media Icons */}
              {[
                { href: "#", icon: <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" /> },
                { href: "#", icon: <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" /> },
                { href: "#", icon: <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" /> },
                { href: "#", icon: <path fillRule="evenodd" d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z" clipRule="evenodd" /> }
              ].map((social, i) => (
                <a key={i} href={social.href} className="text-gray-600 hover:text-gray-300 transition-colors relative group">
                  <div className="relative z-10">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      {social.icon}
                    </svg>
                  </div>
                  <motion.div
                    className="absolute inset-0 rounded-full bg-gray-800/0 group-hover:bg-gray-800/80 p-1"
                    whileHover={{ boxShadow: '0 0 10px 2px rgba(75,85,99,0.3)' }}
                    transition={{ duration: 0.2 }}
                  />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="flex items-center space-x-2 mb-4">
              <Code size={18} className="text-gray-400" />
              <h3 className="text-gray-200 font-semibold">Quick Links</h3>
            </div>

            <ul className="space-y-2">
              <li><FooterLink to="/about">About Us</FooterLink></li>
              <li><FooterLink to="/projects">Projects</FooterLink></li>
              <li><FooterLink to="/research">Research</FooterLink></li>
              <li><FooterLink to="/contact">Contact</FooterLink></li>
              {/* <li>
                <Link to="/delta" className="text-gray-500 hover:text-gray-300 transition-colors flex items-center group" aria-label="Delta">
                  <span className="relative text-lg">
                    Δ
                    <motion.div
                      className="absolute left-0 right-0 h-px bg-gray-700/50 bottom-0 origin-left"
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </span>
                  <motion.div
                    className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, repeatType: 'loop', ease: 'easeInOut' }}
                  >
                    <Hexagon size={8} className="text-gray-500" />
                  </motion.div>
                </Link> 
              </li> */}
            </ul>
          </motion.div>

          {/* Programs & Legal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Legal Section */}
            <div className="flex items-center space-x-2 mb-4">
              <Lock size={18} className="text-gray-400" />
              <h3 className="text-gray-200 font-semibold">Legal</h3>
            </div>

            <ul className="space-y-2">
              <li><FooterLink to="/ai-ethics">AI Ethics Policy</FooterLink></li>
              <li><FooterLink to="/privacy">Privacy Policy</FooterLink></li>
              <li><FooterLink to="/terms">Terms of Service</FooterLink></li>
              <li><FooterLink to="/security">Security</FooterLink></li>
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="flex items-center space-x-2 mb-4">
              <Mail size={18} className="text-gray-400" />
              <h3 className="text-gray-200 font-semibold">Contact Us</h3>
            </div>

            <ul className="space-y-3">
              <li className="flex items-start group">
                <div className="mr-3 p-1.5 bg-gray-900/50 rounded-md text-gray-500 border border-gray-700/30 group-hover:border-gray-600/50 transition-colors duration-300 mt-0.5">
                  <MapPin size={16} />
                </div>
                <span className="text-gray-500 group-hover:text-gray-400 transition-colors duration-300">
                  351 St Francis St
                  <br />
                  Mobile, AL 36602
                </span>
              </li>

              <li className="flex items-center group">
                <div className="mr-3 p-1.5 bg-gray-900/50 rounded-md text-gray-500 border border-gray-700/30 group-hover:border-gray-600/50 transition-colors duration-300">
                  <Phone size={16} />
                </div>
                <span className="text-gray-500 group-hover:text-gray-300 transition-colors duration-300">
                  Phone: coming soon
                </span>
              </li>

              <li className="flex items-center group">
                <div className="mr-3 p-1.5 bg-gray-900/50 rounded-md text-gray-500 border border-gray-700/30 group-hover:border-gray-600/50 transition-colors duration-300">
                  <Mail size={16} />
                </div>
                <a href="mailto:Contact@ReticuliTech.com" className="text-gray-500 group-hover:text-gray-300 transition-colors duration-300">
                  Contact@ReticuliTech.com
                </a>
              </li>
            </ul>
          </motion.div>
        </div>

        <div className="border-t border-gray-800/30 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <motion.p
            className="text-gray-500 text-sm"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            &copy; {currentYear} Reticuli Technologies. All rights reserved.
          </motion.p>

          <motion.div
            className="mt-4 md:mt-0 flex items-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <div className="mr-2 p-1 bg-gray-900/50 rounded-full border border-gray-700/30">
              <Shield size={16} className="text-gray-400" />
            </div>
            <span className="text-gray-500 text-sm">Early-stage R&D company.</span>
          </motion.div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;